import React, { useState, useMemo } from 'react';
import { calculate } from '../lib/calculations';
import NumberStepper from './NumberStepper';

const fmtMoney = (v, sym = '$') => {
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) >= 10000) return `${sym}${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return `${sym}${v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

const fmtMoneyTight = (v, sym = '$') => {
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 10_000)    return `${sym}${(v / 1000).toFixed(0)}k`;
  if (Math.abs(v) >= 1000)      return `${sym}${(v / 1000).toFixed(1)}k`;
  return `${sym}${v.toFixed(0)}`;
};

const fmtHours = (h) => {
  if (!Number.isFinite(h) || h <= 0) return null;
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 50) return `${h.toFixed(1)} hr`;
  return `${Math.round(h)} hr`;
};

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// ─── Seasonality presets ─────────────────────────────────────────────
//
// Each preset is a 12-element multiplier array (Jan…Dec). The user's
// "base monthly volume" gets multiplied by these to produce per-month
// forecasts. None = flat. Light/Heavy give Q4 a realistic boost
// (handmade peaks in Oct–Dec on Etsy).
const SEASONALITY = {
  none:  {
    label: 'None',
    desc:  'flat year — every month equal',
    mults: [1,1,1,1,1,1,1,1,1,1,1,1],
  },
  light: {
    label: 'Light Q4 boost',
    desc:  'Oct 1.2× · Nov 1.4× · Dec 1.6×',
    mults: [1,1,1,1,1,1,1,1,1,1.2,1.4,1.6],
  },
  heavy: {
    label: 'Heavy Q4 boost',
    desc:  'Oct 1.5× · Nov 2× · Dec 2.5×',
    mults: [1,1,1,1,1,1,1,1,1,1.5,2.0,2.5],
  },
};

// Cumulative profit milestones we try to surface (only those reachable
// within ~10 years are shown).
const MILESTONES = [1000, 5000, 10000, 25000, 100000];

// Single source of truth for scenario presentation. Used by the scenario
// cards, the chart series, and the year-totals legend so they stay in sync.
const SCENARIO_META = {
  conservative: { label: 'Conservative', emoji: '🐢', color: '#1e3a8a' },
  realistic:    { label: 'Realistic',    emoji: '📊', color: '#047857' },
  ambitious:    { label: 'Ambitious',    emoji: '🚀', color: '#7e22ce' },
};
const SCENARIO_KEYS = ['conservative', 'realistic', 'ambitious'];

// ─── Component ───────────────────────────────────────────────────────
export default function ForecastPanel({ inputs, settings, currencySymbol = '$' }) {
  // Three editable scenarios. Each holds a sales-per-month string.
  // Defaults are reasonable starting points; user can edit any.
  const [scenarios, setScenarios] = useState({
    conservative: '10',
    realistic:    '30',
    ambitious:    '60',
  });
  const [activeKey, setActiveKey] = useState('realistic'); // which scenario drives chart + milestones
  const [seasonality, setSeasonality] = useState('none');  // 'none' | 'light' | 'heavy'

  // Per-item single-unit calc — used to project everything by multiplication.
  // We force separate-order math (forecasts are about many independent buyers).
  const single = useMemo(() => {
    if (!inputs?.price) return null;
    const r = calculate(
      { ...inputs, quantity: '1' },
      { ...settings, orderType: 'separate' }
    );
    return (r && r.revenue > 0) ? r : null;
  }, [inputs, settings]);

  const sym = single?.currencySymbol || currencySymbol;
  const hasTax = (settings?.taxRate || 0) > 0;

  // Build a projection for a given monthly base.
  const projectFor = (monthlyBase) => {
    if (!single || monthlyBase <= 0) return null;
    return {
      sales:     single,                              // per-unit calc
      monthly:   {
        sales:      monthlyBase,
        revenue:    single.revenue * monthlyBase,
        etsyFees:   single.totalEtsyFees * monthlyBase,
        cost:       single.totalCost * monthlyBase,
        profit:     single.profit * monthlyBase,
        afterTax:   (single.afterTaxProfit ?? single.profit) * monthlyBase,
        laborHours: (single.totalLaborHours || 0) * monthlyBase,
      },
      yearly:    {
        sales:      monthlyBase * 12,
        revenue:    single.revenue * monthlyBase * 12,
        etsyFees:   single.totalEtsyFees * monthlyBase * 12,
        cost:       single.totalCost * monthlyBase * 12,
        profit:     single.profit * monthlyBase * 12,
        afterTax:   (single.afterTaxProfit ?? single.profit) * monthlyBase * 12,
        laborHours: (single.totalLaborHours || 0) * monthlyBase * 12,
      },
    };
  };

  // Per-scenario projection
  const scenarioProjections = useMemo(() => {
    const out = {};
    for (const k of Object.keys(scenarios)) {
      const base = parseFloat(scenarios[k]) || 0;
      out[k] = projectFor(base);
    }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarios, single]);

  const activeProjection = scenarioProjections[activeKey];

  // Apply seasonality to ALL three scenarios in one pass — the chart
  // overlays them as grouped bars and the year-totals legend reads each.
  // Backward-compatible fields (yearProfit, monthlyProfits, etc.) point at
  // the active scenario so downstream blocks (milestones, headers) need no
  // changes.
  const seasonalSeries = useMemo(() => {
    if (!single) return null;
    const mults = SEASONALITY[seasonality].mults;

    const series = SCENARIO_KEYS.map(key => {
      const base = parseFloat(scenarios[key]) || 0;
      const monthlyProfits = mults.map(m => single.profit * base * m);
      const yearProfit = monthlyProfits.reduce((a, b) => a + b, 0);
      const flatYearProfit = single.profit * base * 12;
      return {
        key,
        ...SCENARIO_META[key],
        base,
        monthlyProfits,
        yearProfit,
        flatYearProfit,
        seasonalLift: yearProfit - flatYearProfit,
      };
    });

    const active = series.find(s => s.key === activeKey) || series[0];

    return {
      mults,
      series,
      active,
      // Backward-compat aliases for existing references throughout this file
      monthlyProfits: active.monthlyProfits,
      yearProfit: active.yearProfit,
      flatYearProfit: active.flatYearProfit,
      seasonalLift: active.seasonalLift,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [single, scenarios, activeKey, seasonality]);

  // Milestone timeline — when does cumulative profit cross each threshold?
  // Walks month by month (with seasonality), capping at 120 months (10y).
  const milestones = useMemo(() => {
    if (!single || !seasonalSeries) return null;
    const base = parseFloat(scenarios[activeKey]) || 0;
    if (base <= 0 || single.profit <= 0) return [];
    const yearProfit = seasonalSeries.yearProfit;
    if (yearProfit <= 0) return [];
    const out = [];
    for (const target of MILESTONES) {
      // Walk months until cumulative crosses target.
      // For speed, jump in whole years first, then refine within the year.
      const fullYears = Math.floor(target / yearProfit);
      let cumulative = fullYears * yearProfit;
      let monthIndex = fullYears * 12;
      if (monthIndex > 120) { out.push({ target, monthsAway: null, beyond: true }); continue; }
      const mults = seasonalSeries.mults;
      while (monthIndex < 120 && cumulative < target) {
        cumulative += single.profit * base * mults[monthIndex % 12];
        monthIndex++;
      }
      if (cumulative >= target) {
        out.push({ target, monthsAway: monthIndex });
      } else {
        out.push({ target, monthsAway: null, beyond: true });
      }
    }
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [single, scenarios, activeKey, seasonalSeries]);

  // ─── Empty-state ────────────────────────────────────────
  if (!single) {
    return (
      <div className="sketch-card">
        <h2 className="font-display text-2xl text-ink mb-1">🗓️ Growth Planner</h2>
        <p className="font-body text-sm text-ink/78 mb-4">
          Calculate something first, then map out conservative, realistic, and
          ambitious growth paths for this product.
        </p>
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────
  return (
    <div className="sketch-card">
      <h2 className="font-display text-2xl text-ink mb-1">🗓️ Growth Planner</h2>
      <p className="font-body text-sm text-ink/78 mb-4">
        Map three growth paths, layer in seasonality, and see when you'd hit each milestone.
      </p>

      {/* ─── 3-Scenario Planner ─── */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <p className="font-body text-sm text-ink" style={{ fontWeight: 700 }}>
            🎯 Three growth scenarios
          </p>
          <p className="font-body text-xs text-ink/72">
            click a card to make it the active plan
          </p>
        </div>
        <div
          style={{
            display: 'grid',
            // Auto-collapse to 1 column at narrow widths — keeps inputs usable on mobile
            gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
            gap: '0.55rem',
          }}
        >
          <ScenarioCard
            keyId="conservative"
            label="Conservative"
            emoji="🐢"
            tone="#1e3a8a"
            value={scenarios.conservative}
            active={activeKey === 'conservative'}
            onChange={(v) => setScenarios(s => ({ ...s, conservative: v }))}
            onActivate={() => setActiveKey('conservative')}
            projection={scenarioProjections.conservative}
            sym={sym}
            hasTax={hasTax}
          />
          <ScenarioCard
            keyId="realistic"
            label="Realistic"
            emoji="📊"
            tone="#047857"
            value={scenarios.realistic}
            active={activeKey === 'realistic'}
            onChange={(v) => setScenarios(s => ({ ...s, realistic: v }))}
            onActivate={() => setActiveKey('realistic')}
            projection={scenarioProjections.realistic}
            sym={sym}
            hasTax={hasTax}
          />
          <ScenarioCard
            keyId="ambitious"
            label="Ambitious"
            emoji="🚀"
            tone="#7e22ce"
            value={scenarios.ambitious}
            active={activeKey === 'ambitious'}
            onChange={(v) => setScenarios(s => ({ ...s, ambitious: v }))}
            onActivate={() => setActiveKey('ambitious')}
            projection={scenarioProjections.ambitious}
            sym={sym}
            hasTax={hasTax}
          />
        </div>
        <p className="font-body text-xs text-ink/72 mt-1.5">
          Tip: most new shops sell 5–20/month. Established shops, 30–100+. Edit any number.
        </p>
      </div>

      {/* ─── Seasonality preset ─── */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
          <p className="font-body text-sm text-ink" style={{ fontWeight: 700 }}>
            🍂 Seasonality
          </p>
          <p className="font-body text-xs text-ink/72">
            Etsy peaks in Q4 — apply a holiday multiplier
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
          {Object.entries(SEASONALITY).map(([key, p]) => (
            <button
              key={key}
              type="button"
              onClick={() => setSeasonality(key)}
              title={p.desc}
              style={{
                padding: '0.32rem 0.7rem',
                background: seasonality === key ? '#b45309' : '#fefdf8',
                color: seasonality === key ? '#fefdf8' : '#1a1a2e',
                border: `1.5px solid ${seasonality === key ? '#b45309' : 'rgba(26,26,46,0.18)'}`,
                borderRadius: '0.5rem',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.86rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.12s',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
        {seasonality !== 'none' && seasonalSeries && seasonalSeries.seasonalLift > 0 && (
          <p className="font-body text-xs text-ink/72 mt-1.5">
            {SEASONALITY[seasonality].desc} ·{' '}
            <strong style={{ color: '#b45309' }}>
              +{fmtMoney(seasonalSeries.seasonalLift, sym)}/yr
            </strong>{' '}
            vs flat year on the {activeKey} scenario.
          </p>
        )}
      </div>

      {/* ─── 12-month chart with all 3 scenarios overlaid ─── */}
      {seasonalSeries && (
        <div
          className="mb-4"
          style={{
            padding: '0.85rem 1rem',
            background: 'rgba(30, 58, 138, 0.04)',
            border: '1px solid rgba(30, 58, 138, 0.18)',
            borderRadius: '0.625rem',
          }}
        >
          <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
            <p className="font-body text-sm text-ink" style={{ fontWeight: 700 }}>
              📊 12-month profit projection
            </p>
            <p className="font-body text-xs text-ink/72">
              All scenarios · {SEASONALITY[seasonality].label}
            </p>
          </div>
          <MonthlyBarChart
            series={seasonalSeries.series}
            activeKey={activeKey}
            mults={seasonalSeries.mults}
            sym={sym}
          />
          {/* Year totals legend — color-keyed, ★ on active */}
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              marginTop: '0.55rem',
              paddingTop: '0.55rem',
              borderTop: '1px dashed rgba(26,26,46,0.15)',
            }}
          >
            <span className="font-body text-xs text-ink/68" style={{ fontWeight: 700, letterSpacing: '0.04em', alignSelf: 'center', marginRight: '0.15rem' }}>
              YEAR TOTALS:
            </span>
            {seasonalSeries.series.map(s => {
              const isActive = s.key === activeKey;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setActiveKey(s.key)}
                  title={`Make ${s.label} the active scenario`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.32rem',
                    padding: '0.18rem 0.55rem',
                    background: isActive ? `${s.color}15` : '#fefdf8',
                    border: `1.5px solid ${isActive ? s.color : 'rgba(26,26,46,0.15)'}`,
                    borderRadius: '999px',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.78rem',
                    cursor: 'pointer',
                    transition: 'all 0.12s',
                    color: '#1a1a2e',
                  }}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: s.color,
                      flexShrink: 0,
                      opacity: isActive ? 1 : 0.55,
                    }}
                  />
                  <span style={{ fontWeight: 600 }}>{s.label}</span>
                  <span
                    className="font-numbers"
                    style={{ fontWeight: 700, color: s.color, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {fmtMoneyTight(s.yearProfit, sym)}
                  </span>
                  {isActive && (
                    <span style={{ fontSize: '0.7rem', color: s.color, marginLeft: '0.05rem' }} aria-hidden="true">★</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Milestone timeline ─── */}
      {milestones && milestones.length > 0 && (
        <div
          className="mb-4"
          style={{
            padding: '0.85rem 1rem',
            background: 'rgba(4, 120, 87, 0.05)',
            border: '1px solid rgba(4, 120, 87, 0.25)',
            borderRadius: '0.625rem',
          }}
        >
          <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
            <p className="font-body text-sm text-ink" style={{ fontWeight: 700 }}>
              🏁 Milestone timeline
            </p>
            <p className="font-body text-xs text-ink/72">
              cumulative profit · {activeKey} + seasonality
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.32rem' }}>
            {milestones.map((m) => (
              <MilestoneRow key={m.target} milestone={m} sym={sym} />
            ))}
          </div>
        </div>
      )}

      {/* ─── Time investment context ─── */}
      {activeProjection && activeProjection.yearly.laborHours > 0 && (
        <div
          style={{
            padding: '0.75rem 1rem',
            background: 'rgba(180, 83, 9, 0.06)',
            border: '1px solid rgba(180, 83, 9, 0.2)',
            borderRadius: '0.625rem',
            marginBottom: '0.75rem',
          }}
        >
          <p className="font-body text-sm text-ink" style={{ fontWeight: 600, lineHeight: 1.5 }}>
            ⏱️ Time investment ({activeKey}):{' '}
            <span style={{ color: '#b45309' }}>
              {fmtHours(activeProjection.monthly.laborHours)}/month
            </span>
            {' · '}
            <span style={{ color: '#b45309' }}>
              {fmtHours(activeProjection.yearly.laborHours)}/year
            </span>
          </p>
          <p className="font-body text-xs text-ink/78 mt-0.5">
            That's about <strong>{(activeProjection.yearly.laborHours / 8).toFixed(0)} full days</strong> of work — at an effective hourly rate of{' '}
            <strong style={{ color: '#047857' }}>
              {sym}{(activeProjection.yearly.profit / activeProjection.yearly.laborHours).toFixed(2)}/hour
            </strong>.
          </p>
        </div>
      )}

      {/* ─── Etsy take ratio context ─── */}
      {activeProjection && (
        <div
          style={{
            padding: '0.75rem 1rem',
            background: 'rgba(30, 58, 138, 0.04)',
            border: '1px solid rgba(30, 58, 138, 0.18)',
            borderRadius: '0.625rem',
          }}
        >
          <p className="font-body text-sm text-ink" style={{ fontWeight: 600, lineHeight: 1.5 }}>
            💸 Etsy will collect about{' '}
            <span style={{ color: '#1e3a8a' }}>
              {fmtMoney(activeProjection.yearly.etsyFees, sym)}/year
            </span>
            {' '}from this product alone (at {activeKey} pace).
          </p>
          <p className="font-body text-xs text-ink/78 mt-0.5">
            {((activeProjection.yearly.etsyFees / activeProjection.yearly.revenue) * 100).toFixed(1)}% of your revenue goes to platform fees.
          </p>
        </div>
      )}

      {/* (Break-even calculator has been promoted to its own component —
          see BreakEvenBar.js, rendered as a standalone bar in App.js
          right below the Profit-Price Finder bar.) */}

      <p className="font-note text-xs text-ink/78 mt-4">
        ~ all projections track your current pricing · taxes shown if you've enabled set-aside ~
      </p>
    </div>
  );
}

// ─── Scenario card sub-component ─────────────────────────────────────
function ScenarioCard({ keyId, label, emoji, tone, value, active, onChange, onActivate, projection, sym, hasTax }) {
  const profitMonthly = projection?.monthly?.profit ?? 0;
  const profitYearly = projection?.yearly?.profit ?? 0;
  const showAfterTax = hasTax && projection;

  return (
    <div
      onClick={onActivate}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(); } }}
      style={{
        padding: '0.75rem 0.8rem',
        background: active ? `${tone}10` : '#fefdf8',
        border: active ? `2px solid ${tone}` : '1.5px solid rgba(26,26,46,0.15)',
        borderRadius: '0.625rem',
        cursor: 'pointer',
        transition: 'all 0.15s',
        position: 'relative',
        boxShadow: active ? `0 1px 2px ${tone}33, 0 4px 10px ${tone}1a` : '0 1px 2px rgba(26,26,46,0.04)',
        minWidth: 0,
      }}
    >
      {active && (
        <div
          style={{
            position: 'absolute',
            top: '-9px',
            right: '0.6rem',
            background: tone,
            color: '#fefdf8',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.66rem',
            fontWeight: 700,
            padding: '0.12rem 0.45rem',
            borderRadius: '0.3rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          ★ Active
        </div>
      )}

      <div className="flex items-center gap-1.5 mb-1.5">
        <span style={{ fontSize: '1.05rem' }} aria-hidden="true">{emoji}</span>
        <span
          className="font-body"
          style={{ fontSize: '0.78rem', color: tone, fontWeight: 700, letterSpacing: '0.04em' }}
        >
          {label.toUpperCase()}
        </span>
      </div>

      {/* Editable volume */}
      <div onClick={(e) => e.stopPropagation()} style={{ marginBottom: '0.5rem' }}>
        <NumberStepper
          value={value}
          onChange={onChange}
          step={5}
          min={0}
          placeholder="0"
          inputClassName="sketch-input"
          inputStyle={{ fontSize: '0.95rem', padding: '0.4rem 0.55rem' }}
        />
        <p
          className="font-body"
          style={{ fontSize: '0.7rem', color: 'rgba(26,26,46,0.6)', marginTop: '0.15rem', textAlign: 'left' }}
        >
          sales / month
        </p>
      </div>

      {/* Headline profit per month */}
      <div>
        <p
          className="font-numbers"
          style={{
            fontSize: '1.15rem', fontWeight: 700,
            color: profitMonthly >= 0 ? '#047857' : '#9f1239',
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.02em', lineHeight: 1.05, margin: 0,
          }}
        >
          {fmtMoneyTight(showAfterTax ? projection.monthly.afterTax : profitMonthly, sym)}/mo
        </p>
        <p
          className="font-body"
          style={{ fontSize: '0.74rem', color: 'rgba(26,26,46,0.7)', marginTop: '0.1rem' }}
        >
          {fmtMoneyTight(showAfterTax ? projection.yearly.afterTax : profitYearly, sym)}/yr {hasTax ? 'kept' : 'profit'}
        </p>
      </div>
    </div>
  );
}

// ─── Monthly bar chart — grouped bars per month, all scenarios overlaid ─
//
// Per month: thin colored bars side-by-side, one per scenario, in a fixed
// order (conservative → realistic → ambitious). Active scenario is full
// opacity; the others fade to ~50% so they read as context. Q4 months get
// a subtle warm background tint when seasonality is on, so the seasonal
// indicator survives without conflicting with scenario colors.
function MonthlyBarChart({ series, activeKey, mults, sym }) {
  const monthCount = 12;
  const allValues = series.flatMap(s => s.monthlyProfits);
  const max = Math.max(...allValues, 0.01);

  const W = 560;
  const H = 140;
  const PADX = 12;
  const PADY = 24;
  const slot = (W - PADX * 2) / monthCount;
  const numSeries = series.length;
  const groupOuter = 4;       // padding inside each month slot, left & right
  const barGap = 1.5;         // gap between scenario bars within a month group
  const groupInnerW = slot - groupOuter * 2;
  const barW = (groupInnerW - barGap * (numSeries - 1)) / numSeries;

  const activeIdx = series.findIndex(s => s.key === activeKey);
  const activeSeries = series[activeIdx >= 0 ? activeIdx : 0];

  return (
    <div style={{ width: '100%', overflow: 'hidden' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height: 'auto', display: 'block' }}
        role="img"
        aria-label="12-month profit projection — three scenarios overlaid"
      >
        {/* Q4 background tint — only painted when a multiplier > 1 */}
        {mults && mults.map((m, i) => {
          if (m <= 1) return null;
          return (
            <rect
              key={`q4-${i}`}
              x={PADX + i * slot}
              y={2}
              width={slot}
              height={H - PADY - 2}
              fill="rgba(180, 83, 9, 0.09)"
              rx={3}
            />
          );
        })}

        {/* Baseline */}
        <line
          x1={PADX} y1={H - PADY} x2={W - PADX} y2={H - PADY}
          stroke="rgba(26,26,46,0.2)" strokeWidth="1"
        />

        {/* Per-month grouped bars + labels */}
        {Array.from({ length: monthCount }).map((_, monthI) => {
          const slotX = PADX + monthI * slot;
          // Active bar geometry — used for the value label
          const activeV = activeSeries.monthlyProfits[monthI] || 0;
          const activeH = max > 0 ? Math.max(activeV > 0 ? 2 : 0, (activeV / max) * (H - PADY * 2)) : 0;
          const activeBarX = slotX + groupOuter + activeIdx * (barW + barGap);
          const activeBarY = H - PADY - activeH;

          return (
            <g key={monthI}>
              {series.map((s, sI) => {
                const v = s.monthlyProfits[monthI] || 0;
                const h = max > 0 ? Math.max(v > 0 ? 2 : 0, (v / max) * (H - PADY * 2)) : 0;
                const x = slotX + groupOuter + sI * (barW + barGap);
                const y = H - PADY - h;
                const isActive = s.key === activeKey;
                return (
                  <rect
                    key={s.key}
                    x={x} y={y} width={barW} height={h}
                    fill={s.color}
                    opacity={isActive ? 1 : 0.42}
                    rx={1.5}
                  />
                );
              })}

              {/* Month label */}
              <text
                x={slotX + slot / 2}
                y={H - PADY + 14}
                textAnchor="middle"
                fontSize="9"
                fontFamily="Inter, sans-serif"
                fill="rgba(26,26,46,0.65)"
              >
                {MONTH_LABELS[monthI]}
              </text>

              {/* Value label only on the active scenario, only when there's room */}
              {activeH > 24 && activeV > 0 && (
                <text
                  x={activeBarX + barW / 2}
                  y={activeBarY - 3}
                  textAnchor="middle"
                  fontSize="8.5"
                  fontFamily="Inter, sans-serif"
                  fontWeight="700"
                  fill={activeSeries.color}
                >
                  {fmtMoneyTight(activeV, sym)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── Milestone row ───────────────────────────────────────────────────
function MilestoneRow({ milestone, sym }) {
  const { target, monthsAway, beyond } = milestone;
  // Compact, single-line format for every duration so milestone rows
  // stay uniform in height on narrow screens. Was a mix of "X months"
  // (which wrapped on mobile) and "Xy Ymo" (which didn't); now both
  // are short and consistent: "1mo / 8mo / 1y 4mo / 3y 2mo".
  const formatMonths = (m) => {
    if (m == null) return null;
    if (m <= 1) return '~1mo';
    if (m < 12) return `${m}mo`;
    const years = Math.floor(m / 12);
    const months = m % 12;
    if (months === 0) return `${years}y`;
    return `${years}y ${months}mo`;
  };
  const dateLabel = (m) => {
    if (m == null) return '';
    const d = new Date();
    d.setMonth(d.getMonth() + m);
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.6rem',
        padding: '0.4rem 0.55rem',
        background: '#fefdf8',
        border: '1px solid rgba(26,26,46,0.1)',
        borderRadius: '0.4rem',
        opacity: beyond ? 0.55 : 1,
      }}
    >
      <span
        className="font-numbers"
        style={{ fontSize: '0.95rem', fontWeight: 700, color: '#047857', fontVariantNumeric: 'tabular-nums', minWidth: '4.5rem' }}
      >
        {fmtMoneyTight(target, sym)}
      </span>
      <span
        className="font-body"
        style={{ fontSize: '0.78rem', color: 'rgba(26,26,46,0.6)', flexShrink: 0 }}
      >
        cumulative profit
      </span>
      <span style={{ flex: 1, minWidth: 0 }} />
      {beyond ? (
        <span
          className="font-body"
          style={{
            fontSize: '0.82rem',
            color: 'rgba(26,26,46,0.5)',
            fontStyle: 'italic',
            whiteSpace: 'nowrap',
          }}
        >
          &gt; 10 years
        </span>
      ) : (
        <>
          <span
            className="font-body"
            style={{
              fontSize: '0.86rem',
              color: '#1a1a2e',
              fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            {formatMonths(monthsAway)}
          </span>
          <span
            className="font-body"
            style={{
              fontSize: '0.78rem',
              color: 'rgba(26,26,46,0.6)',
              minWidth: '5.2rem',
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}
          >
            ({dateLabel(monthsAway)})
          </span>
        </>
      )}
    </div>
  );
}
